import { useState } from 'react';
import { FileText } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent, Badge } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import {
  useDocumentsByType,
  useDocumentStatistics,
  type AnnouncementTab,
  type FeedDocument,
} from '@/api/dashboard-feeds';
import { formatDayMonth } from '@/lib/format';
import { WidgetCard } from './WidgetCard';
import { FeedRow, FeedScroll, FeedSkeleton, InitialsAvatar } from './feed-parts';

/**
 * Company notices & documents, grouped by document TYPE. One tab per type, so a
 * document appears in exactly one tab (no duplication). The internal asset
 * library (`/assets/dashboard?document_group=`) is intentionally NOT shown here:
 * it returns the same records re-grouped (e.g. REGULATION == PROCEDURE_GUIDE),
 * which duplicated this feed. Tab counts come from `/documents/statistical-dashboard`.
 */
const TABS: { key: AnnouncementTab; label: string }[] = [
  { key: 'NOTIFICATION', label: 'Notifications' },
  { key: 'REGULATION', label: 'Regulations' },
  { key: 'OTHER', label: 'Guides' },
];

function DocumentFeed({ docType }: { docType: AnnouncementTab }) {
  const { data = [], isLoading } = useDocumentsByType(docType);
  if (isLoading) return <FeedSkeleton />;
  if (data.length === 0) return <EmptyState icon={<FileText size={22} />} size="sm" title="No documents yet" />;
  return (
    <FeedScroll>
      {data.map((d: FeedDocument) => (
        <FeedRow
          key={d.document_id}
          leading={<InitialsAvatar name={d.employee_name || d.name_file} />}
          title={d.name_file?.trim() || 'Untitled'}
          detail={d.descriptions || undefined}
          meta={
            <>
              {d.employee_name || 'Unknown'}
              {d.created_at ? ` · ${formatDayMonth(d.created_at)}` : ''}
            </>
          }
        />
      ))}
    </FeedScroll>
  );
}

export function DocumentsWidget() {
  const [tab, setTab] = useState<AnnouncementTab>('NOTIFICATION');
  const { data: stats } = useDocumentStatistics();
  const countOf = (key: AnnouncementTab): number => stats?.[key]?.total_count ?? 0;

  return (
    <WidgetCard title="Documents & Notices" description="Company notices, regulations & guides" bodyPadding={false}>
      <Tabs value={tab} onValueChange={(v) => setTab(v as AnnouncementTab)}>
        <div style={{ padding: 'var(--vn-space-3) var(--vn-space-4) 0' }}>
          <TabsList>
            {TABS.map((t) => {
              const count = countOf(t.key);
              return (
                <TabsTrigger key={t.key} value={t.key}>
                  {t.label}
                  <Badge
                    tone={tab === t.key ? 'primary' : 'neutral'}
                    appearance="subtle"
                    size="sm"
                    style={{ marginLeft: 'var(--vn-space-2)' }}
                  >
                    {count}
                  </Badge>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>
        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key}>
            <DocumentFeed docType={t.key} />
          </TabsContent>
        ))}
      </Tabs>
    </WidgetCard>
  );
}
